import React from 'react';
import { Row, Col, Form, Select, Tag, Statistic, Divider, Alert } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

// ✅ Sevkiyat Lojistik Başarısı - Documents seçenekleri skorları çakıştığı için
// (İnvoice/Labelling ikisi de 5) Select value'su olarak benzersiz key kullanıyoruz.
export const DOCUMENTS_SCORE_MAP = {
    all_conform: 20,
    invoice_nonconform: 5,
    labelling_nonconform: 5,
    packing_list_nonconform: 3,
    delivery_note_nonconform: 2,
};

// ✅ Sevkiyat Lojistik Başarısı toplamı: Deadline + Damage + Documents
export const getShipmentLogisticsScore = (values = {}) =>
    (values.deadlineScore ?? 0) +
    (values.damageScore ?? 0) +
    (DOCUMENTS_SCORE_MAP[values.documentsScore] ?? 0);

// ✅ Skor bazlı tag rengi/ikonu (skorlar 0-40 arası, yüksek = iyi)
const getScoreTagColor = (score) => {
    if (score >= 20) return 'success';
    if (score >= 7) return 'warning';
    return 'error';
};

const getScoreTagIcon = (score) => {
    if (score >= 20) return <CheckCircleOutlined />;
    if (score >= 7) return <WarningOutlined />;
    return <CloseCircleOutlined />;
};

/**
 * Sevkiyat Lojistik Başarısı - Deadline / Damage / Documents dropdown'ları + canlı toplam.
 * `SupplierPerformanceDataEntry.js` (create) ve `SupplierPerformanceDataList.js`
 * (edit modal) arasında paylaşılır. `form` prop'u ile hangi Form instance'ına
 * bağlanacağı belirlenir (Form.useWatch bir form instance'ı gerektirir).
 */
const ShipmentLogisticsScoreFields = ({ form, note }) => {
    const { t } = useTranslation();

    const deadlineScoreWatch = Form.useWatch('deadlineScore', form);
    const damageScoreWatch = Form.useWatch('damageScore', form);
    const documentsScoreWatch = Form.useWatch('documentsScore', form);
    const shipmentLogisticsTotal = getShipmentLogisticsScore({
        deadlineScore: deadlineScoreWatch,
        damageScore: damageScoreWatch,
        documentsScore: documentsScoreWatch,
    });

    return (
        <>
            <Divider orientation="left" style={{ fontSize: '14px', color: '#8c8c8c' }}>
                {t('shipment_logistics_success')}
            </Divider>

            {note && (
                <Alert
                    type="info"
                    showIcon
                    message={note}
                    style={{ marginBottom: '16px' }}
                />
            )}

            <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item
                        name="deadlineScore"
                        label={t('shipment_deadline')}
                        rules={[{ required: true, message: t('please_select_shipment_deadline') }]}
                    >
                        <Select placeholder={t('select_shipment_deadline')} size="large">
                            <Option value={40}>
                                <Tag color={getScoreTagColor(40)} icon={getScoreTagIcon(40)}>40 - {t('deadline_option_yes')}</Tag>
                            </Option>
                            <Option value={5}>
                                <Tag color={getScoreTagColor(5)} icon={getScoreTagIcon(5)}>5 - {t('deadline_option_missed_1')}</Tag>
                            </Option>
                            <Option value={7}>
                                <Tag color={getScoreTagColor(7)} icon={getScoreTagIcon(7)}>7 - {t('deadline_option_missed_2')}</Tag>
                            </Option>
                            <Option value={10}>
                                <Tag color={getScoreTagColor(10)} icon={getScoreTagIcon(10)}>10 - {t('deadline_option_missed_3')}</Tag>
                            </Option>
                            <Option value={20}>
                                <Tag color={getScoreTagColor(20)} icon={getScoreTagIcon(20)}>20 - {t('deadline_option_missed_4')}</Tag>
                            </Option>
                            <Option value={0}>
                                <Tag color={getScoreTagColor(0)} icon={getScoreTagIcon(0)}>0 - {t('deadline_option_no')}</Tag>
                            </Option>
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                    <Form.Item
                        name="damageScore"
                        label={t('shipment_damage')}
                        rules={[{ required: true, message: t('please_select_shipment_damage') }]}
                    >
                        <Select placeholder={t('select_shipment_damage')} size="large">
                            <Option value={40}>
                                <Tag color={getScoreTagColor(40)} icon={getScoreTagIcon(40)}>40 - {t('damage_option_none')}</Tag>
                            </Option>
                            <Option value={3}>
                                <Tag color={getScoreTagColor(3)} icon={getScoreTagIcon(3)}>3 - {t('damage_option_1_5')}</Tag>
                            </Option>
                            <Option value={7}>
                                <Tag color={getScoreTagColor(7)} icon={getScoreTagIcon(7)}>7 - {t('damage_option_6_10')}</Tag>
                            </Option>
                            <Option value={10}>
                                <Tag color={getScoreTagColor(10)} icon={getScoreTagIcon(10)}>10 - {t('damage_option_11_15')}</Tag>
                            </Option>
                            <Option value={20}>
                                <Tag color={getScoreTagColor(20)} icon={getScoreTagIcon(20)}>20 - {t('damage_option_16_20')}</Tag>
                            </Option>
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                    <Form.Item
                        name="documentsScore"
                        label={t('shipment_documents')}
                        rules={[{ required: true, message: t('please_select_shipment_documents') }]}
                    >
                        <Select placeholder={t('select_shipment_documents')} size="large">
                            <Option value="all_conform">
                                <Tag color={getScoreTagColor(DOCUMENTS_SCORE_MAP.all_conform)} icon={getScoreTagIcon(DOCUMENTS_SCORE_MAP.all_conform)}>
                                    {DOCUMENTS_SCORE_MAP.all_conform} - {t('documents_option_all_conform')}
                                </Tag>
                            </Option>
                            <Option value="invoice_nonconform">
                                <Tag color={getScoreTagColor(DOCUMENTS_SCORE_MAP.invoice_nonconform)} icon={getScoreTagIcon(DOCUMENTS_SCORE_MAP.invoice_nonconform)}>
                                    {DOCUMENTS_SCORE_MAP.invoice_nonconform} - {t('documents_option_invoice_nonconform')}
                                </Tag>
                            </Option>
                            <Option value="labelling_nonconform">
                                <Tag color={getScoreTagColor(DOCUMENTS_SCORE_MAP.labelling_nonconform)} icon={getScoreTagIcon(DOCUMENTS_SCORE_MAP.labelling_nonconform)}>
                                    {DOCUMENTS_SCORE_MAP.labelling_nonconform} - {t('documents_option_labelling_nonconform')}
                                </Tag>
                            </Option>
                            <Option value="packing_list_nonconform">
                                <Tag color={getScoreTagColor(DOCUMENTS_SCORE_MAP.packing_list_nonconform)} icon={getScoreTagIcon(DOCUMENTS_SCORE_MAP.packing_list_nonconform)}>
                                    {DOCUMENTS_SCORE_MAP.packing_list_nonconform} - {t('documents_option_packing_list_nonconform')}
                                </Tag>
                            </Option>
                            <Option value="delivery_note_nonconform">
                                <Tag color={getScoreTagColor(DOCUMENTS_SCORE_MAP.delivery_note_nonconform)} icon={getScoreTagIcon(DOCUMENTS_SCORE_MAP.delivery_note_nonconform)}>
                                    {DOCUMENTS_SCORE_MAP.delivery_note_nonconform} - {t('documents_option_delivery_note_nonconform')}
                                </Tag>
                            </Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col xs={24}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        background: '#fafafa',
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        marginBottom: '16px'
                    }}>
                        <Statistic
                            title={t('shipment_logistics_success')}
                            value={shipmentLogisticsTotal}
                            suffix="%"
                            valueStyle={{
                                fontSize: '24px',
                                color: shipmentLogisticsTotal >= 80 ? '#52c41a' : shipmentLogisticsTotal >= 50 ? '#faad14' : '#f5222d'
                            }}
                        />
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default ShipmentLogisticsScoreFields;
